import { CONSUMER_WEBHOOK_PREVIOUS_SECRET_HOURS, type ConsumerWebhookWithSecret } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { formatDateTime } from '../format';
import { CopyButton } from './CopyButton';

export interface SigningSecretDialogProps {
  open: boolean;
  consumerName: string;
  /** Risposta di attivazione o rigenerazione: l'unica che porta il segreto in chiaro. */
  revealed: ConsumerWebhookWithSecret;
  /**
   * Vero dopo una rigenerazione: il segreto precedente firma ancora per 24 ore, salvo che l'istanza
   * non sappia più leggerlo (`previousSecretExpiresAt: null`).
   */
  rotated: boolean;
  onClose: () => void;
}

/**
 * Il segreto di firma della notifica, mostrato una sola volta come il `client_secret`
 * (`CredentialsDialog`): si chiude solo con «Ho salvato il segreto». Il fuoco va sul segreto, già
 * selezionato, così chi usa la tastiera o un lettore di schermo lo trova subito.
 */
export function SigningSecretDialog({ open, consumerName, revealed, rotated, onClose }: SigningSecretDialogProps) {
  const previousUntil = revealed.previousSecretExpiresAt;

  return (
    <Dialog open={open} fullWidth maxWidth="sm" aria-labelledby="signing-secret-title">
      <DialogTitle id="signing-secret-title">Segreto di firma</DialogTitle>
      <DialogContent>
        <Stack spacing={4} sx={{ pt: 1 }}>
          <Typography>
            {rotated
              ? `Segreto di firma di ${consumerName} rigenerato.`
              : `La notifica di cambio stato di ${consumerName} è attiva.`}{' '}
            Consegna il segreto al team del prodotto, che lo conserva nel proprio secret manager e lo usa per verificare
            la firma di ogni evento.
          </Typography>
          {rotated &&
            (previousUntil ? (
              <Alert severity="info">
                Il segreto precedente resta valido per {CONSUMER_WEBHOOK_PREVIOUS_SECRET_HOURS} ore, fino al{' '}
                {formatDateTime(previousUntil)}: intanto ogni evento porta entrambe le firme, così il prodotto ha il
                tempo di sostituirlo. Poi vale solo il nuovo.
              </Alert>
            ) : (
              <Alert severity="warning">
                Il segreto precedente non era più leggibile da questa istanza: da adesso gli eventi portano solo la
                firma del nuovo. Consegnalo subito al team del prodotto, che fino ad allora non riesce a verificarli.
              </Alert>
            ))}
          <Alert severity="warning">
            Il segreto di firma è visibile <strong>solo adesso</strong>: chiuso questo dialogo non sarà più
            recuperabile. Se va perso, rigeneralo.
          </Alert>
          <TextField
            label="Segreto di firma"
            value={revealed.signingSecret}
            fullWidth
            autoFocus
            onFocus={(event) => event.target.select()}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: <CopyButton value={revealed.signingSecret} label="segreto di firma" />,
                sx: { fontFamily: (theme) => theme.typography.fontFamilyMonospace },
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Ho salvato il segreto
        </Button>
      </DialogActions>
    </Dialog>
  );
}
